import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import { BannerSkeleton } from "./Skeleton";
import { motion } from "framer-motion";
import { useTheme } from "../contexts/ThemeContext";

// ✅ Local images (inside src/assets/img/)
import bannerui from "../assets/img/bannerui.jpg";

const bannerImages = [
  bannerui,
  "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1200&q=80",
  "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1200&q=80",
];

// 🔹 Main Component
export const BoodmoUi = () => {
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const { isDark } = useTheme();

  useEffect(() => {
    let active = true;
    const img = new Image();
    const finishLoading = () => {
      if (active) setImagesLoaded(true);
    };
    img.onload = finishLoading;
    img.onerror = finishLoading;
    img.src = bannerImages[0];

    return () => {
      active = false;
      img.onload = null;
      img.onerror = null;
    };
  }, []);

  if (!imagesLoaded) return <BannerSkeleton />;

  const promoSegments = [
    "OEM, aftermarket & used auto parts in one place",
    "Trusted sellers · Easy returns",
    "Search by vehicle & part number",
  ];
  const promoLine = promoSegments.join("  ·  ");

  /* Left column: replaces “search bar + add car” with offer copy (reference-style layout) */
  const offerBadge = "Limited-time offer";
  const offerHeadline = "Up to 20% off your first parts order";
  const offerHighlightBar =
    "Use code FIRSTSAVE at checkout · Free delivery on orders ₹499+";

  return (
    <motion.section
      className="relative bg-transparent"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <div className="relative flex h-[132px] sm:h-[156px] md:h-[184px] w-full overflow-hidden bg-transparent">
        {/* Left: promo bars (no white page fill — inherits home dark surface) */}
        <div className="relative z-20 flex min-w-0 flex-1 flex-col justify-center gap-1.5 py-2.5 pl-3 pr-2 sm:gap-2 sm:py-3 sm:pl-4 sm:pr-3 md:pl-6 md:pr-4 lg:pl-8">
          <motion.div
            className={
              isDark
                ? "sp-glass flex min-w-0 items-stretch gap-0 overflow-hidden rounded-sparepe-lg shadow-sp-premium sm:rounded-sparepe-xl"
                : "flex min-w-0 items-stretch gap-0 overflow-hidden rounded-xl border border-sky-200 bg-white shadow-md sm:rounded-2xl sm:shadow-sm"
            }
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            role="region"
            aria-label="Promotional offer"
          >
            <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 px-2.5 py-2 sm:px-4 sm:py-2.5 md:py-3">
              <p
                className={`text-[9px] font-bold uppercase tracking-wide sm:text-[10px] md:text-xs ${
                  isDark ? "text-sparepe-accent" : "text-sky-600"
                }`}
              >
                {offerBadge}
              </p>
              <p
                className={`text-[11px] font-semibold leading-snug sm:text-sm md:text-base md:leading-tight ${
                  isDark ? "text-sparepe-ink" : "text-slate-900"
                }`}
              >
                {offerHeadline}
              </p>
            </div>
            <div
              className={`flex shrink-0 items-center justify-center px-2 sm:px-3 md:px-4 ${
                isDark ? "bg-gradient-to-br from-primary-800 to-primary-500" : "bg-sky-600"
              }`}
            >
              <span className="text-center text-[9px] font-bold uppercase leading-tight text-white sm:text-[10px] md:text-xs">
                SPAREPE
              </span>
            </div>
          </motion.div>

          <motion.div
            className={
              isDark
                ? "flex min-h-0 items-center rounded-sparepe border border-sparepe-border-md bg-sparepe-surface/80 px-2.5 py-1.5 shadow-md sm:rounded-sparepe-lg sm:px-3 sm:py-2"
                : "flex min-h-0 items-center rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-1.5 shadow-sm sm:rounded-xl sm:px-3 sm:py-2"
            }
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.12 }}
          >
            <p
              className={`text-[9px] font-semibold uppercase tracking-wide sm:text-[10px] sm:tracking-wider md:text-xs ${
                isDark ? "text-sparepe-accent-dk" : "text-sky-900"
              }`}
            >
              {offerHighlightBar}
            </p>
          </motion.div>
        </div>

        {/* Right: product / vehicle visual — anchored right */}
        <div className="relative h-full w-[34%] shrink-0 min-w-[6.5rem] sm:min-w-[7.5rem] sm:w-[38%] md:w-[40%] md:max-w-[min(42vw,17rem)]">
          <Swiper
            modules={[Autoplay]}
            spaceBetween={0}
            slidesPerView={1}
            loop={true}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            speed={800}
            className="h-full w-full"
          >
            {bannerImages.map((image, index) => (
              <SwiperSlide key={index}>
                <div className="relative flex h-full w-full items-end justify-end bg-transparent">
                  <img
                    loading="lazy"
                    src={image}
                    alt={`Banner ${index + 1}`}
                    className="h-full max-h-full w-full object-contain object-[88%_92%] drop-shadow-[0_8px_28px_rgba(0,0,0,0.35)] sm:object-[94%_88%] md:object-[96%_85%]"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/1200x600?text=Banner';
                    }}
                  />
                  <div
                    className={
                      isDark
                        ? "pointer-events-none absolute inset-y-0 left-0 w-[50%] bg-gradient-to-r from-slate-950 via-slate-950/40 to-transparent"
                        : "pointer-events-none absolute inset-y-0 left-0 w-[45%] bg-gradient-to-r from-white via-white/70 to-transparent"
                    }
                    aria-hidden="true"
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      <div
        className={`relative z-10 overflow-hidden border-t py-2 sm:py-2.5 ${
          isDark
            ? "border-slate-800/80 bg-sky-900"
            : "border-sky-200 bg-sky-100"
        }`}
        aria-label="Promotions and highlights"
      >
        <div className="flex w-max animate-promo-marquee hover:[animation-play-state:paused]">
          <p
            className={`shrink-0 px-6 text-[11px] tracking-wide sm:text-xs md:text-sm whitespace-nowrap ${
              isDark ? "text-sky-50/95" : "text-sky-950"
            }`}
          >
            <span className={`font-semibold ${isDark ? "text-amber-300" : "text-amber-700"}`}>SPAREPE</span>
            <span className={`mx-2 ${isDark ? "text-sky-400" : "text-sky-600"}`}>|</span>
            {promoLine}
          </p>
          <p
            className={`shrink-0 px-6 text-[11px] tracking-wide sm:text-xs md:text-sm whitespace-nowrap ${
              isDark ? "text-sky-50/95" : "text-sky-950"
            }`}
            aria-hidden="true"
          >
            <span className={`font-semibold ${isDark ? "text-amber-300" : "text-amber-700"}`}>SPAREPE</span>
            <span className={`mx-2 ${isDark ? "text-sky-400" : "text-sky-600"}`}>|</span>
            {promoLine}
          </p>
        </div>
      </div>
    </motion.section>
  );
};